import { MODAL } from '../../constants/labels';
import { fmtCurrency } from '../../utils/formatNumber';
import Modal from './Modal';

/**
 * CancelConfirmModal
 * ──────────────────
 * Standard "cancel with reason" dialog used by Pledges, Receipts, and Vouchers.
 *
 * @param {Object}   entity          ENTITIES.pledge / ENTITIES.receipt / etc.
 * @param {Object}   item            The item being cancelled (null = hidden)
 * @param {string}   itemDisplay     Short description shown in the modal body
 * @param {number}   [amount]        Amount to show (optional)
 * @param {string}   reason          Controlled reason text
 * @param {Function} onReasonChange  Setter for reason
 * @param {Function} onConfirm       Called on confirm
 * @param {Function} onClose         Called to close the modal
 * @param {boolean}  [cancelling]    Disables confirm button while in-flight
 * @param {string}   [extraNote]     Extra note under the description (e.g. journal reversal note)
 *
 * Usage:
 *   <CancelConfirmModal
 *     entity={ENTITIES.receipt}
 *     item={cancelModal}
 *     itemDisplay={cancelModal?.receiptNo}
 *     amount={cancelModal?.amountInr}
 *     extraNote={MODAL.cancel.journalEntryNote}
 *     reason={cancelReason}
 *     onReasonChange={setReason}
 *     onConfirm={handleCancel}
 *     onClose={closeCancel}
 *     cancelling={cancelling}
 *   />
 */
export default function CancelConfirmModal({
  entity,
  item,
  itemDisplay,
  amount,
  extraNote,
  reason,
  onReasonChange,
  onConfirm,
  onClose,
  cancelling = false,
  showReason = true,
  title: titleOverride,
  confirmLabel,
}) {
  if (!item) return null;

  const labels = MODAL.cancel;
  const modalTitle = titleOverride ?? labels.title(entity);
  const confirmText = confirmLabel ?? labels.confirm;
  const inFlightText = confirmLabel ? `${confirmLabel}…` : 'Cancelling…';

  return (
    <Modal
      title={modalTitle}
      onClose={onClose}
      actions={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={cancelling}>
            {labels.back}
          </button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={cancelling}>
            {cancelling ? inFlightText : confirmText}
          </button>
        </>
      }
    >
      <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', margin: 0 }}>
        {confirmLabel ? confirmLabel.replace('Confirm ', '') : 'Cancel'} {entity.singular.toLowerCase()}
        {itemDisplay ? <> <strong>{itemDisplay}</strong></> : ''}
        {amount !== undefined ? <> — <strong>{fmtCurrency(amount)}</strong></> : ''}?
      </p>
      {extraNote && (
        <p style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', margin: 0 }}>
          {extraNote}
        </p>
      )}
      {showReason && (
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>{labels.reasonLabel}</label>
          <input
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder={labels.reasonPlaceholder}
            autoFocus
          />
        </div>
      )}
    </Modal>
  );
}
